import { Box, SelectChangeEvent } from '@mui/material';
import {
    FunctionComponent,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { useSelector } from 'react-redux';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { Loader, Dropdown2, Label } from '../../../../shared';
import { getChartReviewsData } from '../../../../utils/reviews';
import { StackedBar } from 'src/shared/Charts/StackedBar';
import { IProjectData, BudgetCategory } from 'src/types/projects';
import ThirdPartyExpenses from './ThirdPartyExpenses';
import {
    BUDGET_PERIOD_OPTIONS,
    DEFAULT_BUDGET_REVIEW_FILTER,
    getBudgetReviewDescription,
    IBudgetReviewFilter
} from 'src/constants/budgetReview';
import { getProjectBudgetReview } from 'src/redux/projects/service';
import { ClientSelectors } from 'src/redux/client/selectors';
import { RootState } from 'src/redux/store';
import {
    chartWrapperStyle,
    DatePickerInput,
    FilterContainer,
    FilterField,
    filterLabelStyle
} from './styled';

type Props = { projectData: IProjectData | null };

const BudgetReview: FunctionComponent<Props> = ({ projectData }: Props) => {
    const dispatch = useAppDispatch();
    const { selectedProjectId } = ClientSelectors();
    const budgetReviews = useMemo(
        () => projectData?.budgetReviews ?? [],
        [projectData?.budgetReviews]
    );
    const isDataLoading = useSelector(
        (state: RootState) => state.projects.isDataLoading
    );
    const [filter, setFilter] = useState<IBudgetReviewFilter>(
        DEFAULT_BUDGET_REVIEW_FILTER
    );
    const [isBudgetLoading, setIsBudgetLoading] = useState(false);

    const fetchBudgetReview = useCallback(
        async (nextFilter: IBudgetReviewFilter) => {
            if (!selectedProjectId) {
                return;
            }

            setIsBudgetLoading(true);
            try {
                await dispatch(
                    getProjectBudgetReview({
                        id: selectedProjectId,
                        ...nextFilter
                    })
                );
            } finally {
                setIsBudgetLoading(false);
            }
        },
        [dispatch, selectedProjectId]
    );

    useEffect(() => {
        setFilter(DEFAULT_BUDGET_REVIEW_FILTER);
    }, [selectedProjectId]);

    const handlePeriodChange = (event: SelectChangeEvent) => {
        const selectedOption = BUDGET_PERIOD_OPTIONS.find(
            (option) => option.label === event.target.value
        );
        if (!selectedOption) {
            return;
        }

        const nextFilter = {
            ...filter,
            periodType: selectedOption.value
        };
        setFilter(nextFilter);
        fetchBudgetReview(nextFilter);
    };

    const handleEndDateChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const nextFilter = {
            ...filter,
            endDate: event.target.value
        };
        setFilter(nextFilter);
        if (event.target.value) {
            fetchBudgetReview(nextFilter);
        }
    };

    const isDefaultPeriod = filter.periodType === 'months';

    const chartData = useMemo(() => {
        const reviews = isDefaultPeriod
            ? budgetReviews
            : budgetReviews.map((review) => ({
                  ...review,
                  budget: review.budget.filter(
                      (item) => item.name !== BudgetCategory.THIRD_PARTY
                  )
              }));
        return getChartReviewsData(reviews);
    }, [budgetReviews, isDefaultPeriod]);

    const selectedPeriodLabel =
        BUDGET_PERIOD_OPTIONS.find(
            (option) => option.value === filter.periodType
        )?.label || BUDGET_PERIOD_OPTIONS[0].label;
    const today = new Date().toISOString().slice(0, 10);
    const showLoader =
        isBudgetLoading || (isDataLoading && budgetReviews.length === 0);

    return (
        <>
            {showLoader ? (
                <Loader />
            ) : (
                <Box sx={chartWrapperStyle}>
                    <FilterContainer>
                        <FilterField>
                            <Label sxOverrides={filterLabelStyle}>
                                Time range
                            </Label>
                            <Dropdown2
                                value={selectedPeriodLabel}
                                onChange={handlePeriodChange}
                                options={BUDGET_PERIOD_OPTIONS.map(
                                    (option) => option.label
                                )}
                                width={160}
                            />
                        </FilterField>
                        <FilterField>
                            <Label sxOverrides={filterLabelStyle}>
                                Count from
                            </Label>
                            <DatePickerInput
                                type="date"
                                value={filter.endDate}
                                max={today}
                                onChange={handleEndDateChange}
                            />
                        </FilterField>
                    </FilterContainer>
                    <StackedBar
                        title={'Cost'}
                        data={chartData}
                        breadCum="Costs / $"
                        description={getBudgetReviewDescription(
                            filter.periodType
                        )}
                    />
                    {isDefaultPeriod && <ThirdPartyExpenses />}
                </Box>
            )}
        </>
    );
};

export default BudgetReview;
